import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Users, UserPlus, Trash2, Loader2, Pencil, X, GitMerge } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { nombre: "", email: "", telefono: "", aliases: "" };

export default function VendedoresModal({ catalog = [], onChange, isAdmin = false }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [dups, setDups] = useState(null);
  const [dupLoading, setDupLoading] = useState(false);
  const [unifying, setUnifying] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.email.trim() || !form.telefono.trim()) {
      toast.error("Nombre y Apellido, Correo y Teléfono son obligatorios");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre.trim(), email: form.email.trim(), telefono: form.telefono.trim(),
        aliases: (form.aliases || "").split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
      };
      if (editingId) {
        const res = await api.put(`/vendedores/catalog/${editingId}`, payload);
        const m = res?.data?.migrated || 0;
        toast.success(m ? `Vendedor actualizado · ${m} referencia(s) migrada(s)` : "Vendedor actualizado");
      } else {
        await api.post("/vendedores/catalog", payload);
        toast.success("Vendedor registrado");
      }
      setForm(EMPTY);
      setEditingId(null);
      await onChange?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.id) {
        const existing = catalog.find((c) => c.id === detail.id);
        toast.error(detail.message || "Ya existe un vendedor con ese nombre o correo", {
          action: existing ? { label: "Abrir existente", onClick: () => startEdit(existing) } : undefined,
        });
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "No se pudo guardar"));
      }
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (v) => {
    setEditingId(v.id);
    setForm({ nombre: v.nombre || "", email: v.email || "", telefono: v.telefono || "", aliases: (v.aliases || []).join(", ") });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/vendedores/catalog/${id}`);
      toast.success("Vendedor eliminado");
      if (editingId === id) cancelEdit();
      await onChange?.();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const loadDups = async () => {
    setDupLoading(true);
    try {
      const res = await api.get("/admin/vendedores/duplicados");
      const groups = res?.data?.groups || [];
      setDups(groups);
      if (groups.length === 0) toast.success("No hay vendedores duplicados.");
    } catch {
      toast.error("No se pudieron cargar los duplicados");
    } finally {
      setDupLoading(false);
    }
  };

  const applyUnify = async () => {
    setUnifying(true);
    try {
      const res = await api.post("/admin/vendedores/unificar");
      toast.success(res?.data?.message || "Vendedores unificados");
      setDups(null);
      await onChange?.();
    } catch {
      toast.error("No se pudieron unificar");
    } finally {
      setUnifying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="vendedores-button" variant="outline" className="h-9 border-emerald-200 text-emerald-800 hover:bg-emerald-50">
          <Users className="h-4 w-4 mr-1.5" /> Vendedores
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="vendedores-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-950">
            <UserPlus className="h-5 w-5 text-emerald-700" /> Registro de Vendedores
          </DialogTitle>
          <DialogDescription>
            Da de alta a tus vendedores antes de generar el informe. Luego podrás seleccionarlos en el filtro.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Nombre y Apellido *</label>
            <Input data-testid="vendedor-nombre" value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej. Roberto González" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Correo Electrónico *</label>
              <Input data-testid="vendedor-email" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="roberto@tecnomyl.com" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Número de Teléfono *</label>
              <Input data-testid="vendedor-telefono" value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="+595 981 000000" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Nombres alternativos (como aparece en los archivos)</label>
            <Input data-testid="vendedor-aliases" value={form.aliases}
              onChange={(e) => setForm({ ...form, aliases: e.target.value })}
              placeholder="Ej. MARCOS ESTEBAN FLORENTIN, M. Florentin" />
            <p className="text-[11px] text-slate-500 mt-0.5">Separá varios con coma. Al confirmar una carga, el nombre del archivo se guarda solo aquí.</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="vendedor-submit" type="submit" disabled={saving} className="flex-1 bg-emerald-700 hover:bg-emerald-800">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : editingId ? <Pencil className="h-4 w-4 mr-1.5" /> : <UserPlus className="h-4 w-4 mr-1.5" />}
              {editingId ? "Guardar cambios" : "Registrar vendedor"}
            </Button>
            {editingId && (
              <Button type="button" data-testid="vendedor-cancel-edit" variant="outline" onClick={cancelEdit} className="border-slate-200">
                <X className="h-4 w-4 mr-1.5" /> Cancelar
              </Button>
            )}
          </div>
        </form>

        <div className="border-t border-slate-100 pt-3 max-h-60 overflow-y-auto">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
            Vendedores registrados ({catalog.length})
          </p>
          {catalog.length === 0 ? (
            <p className="text-sm text-slate-400 py-3 text-center">Aún no hay vendedores registrados.</p>
          ) : (
            <ul className="space-y-1.5" data-testid="vendedores-list">
              {catalog.map((v) => (
                <li key={v.id} className={`flex items-center justify-between gap-2 rounded-md border px-3 py-2 hover:bg-slate-50 ${editingId === v.id ? "border-emerald-300 bg-emerald-50/50" : "border-slate-100"}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{v.nombre}</p>
                    <p className="text-xs text-slate-500 truncate">{v.email} · {v.telefono}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button data-testid={`vendedor-edit-${v.id}`} onClick={() => startEdit(v)}
                      className="text-slate-400 hover:text-emerald-700 p-1" title="Editar">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button data-testid={`vendedor-delete-${v.id}`} onClick={() => remove(v.id)}
                      className="text-slate-400 hover:text-red-600 p-1" title="Eliminar">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {isAdmin && (
          <div className="border-t border-slate-100 pt-3">
            {!dups || dups.length === 0 ? (
              <Button type="button" variant="outline" size="sm" data-testid="vendedores-check-dups"
                onClick={loadDups} disabled={dupLoading}
                className="w-full border-amber-200 text-amber-800 hover:bg-amber-50">
                {dupLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <GitMerge className="h-4 w-4 mr-1.5" />}
                Revisar vendedores duplicados
              </Button>
            ) : (
              <div className="space-y-2" data-testid="vendedores-dups-panel">
                <p className="text-xs font-semibold text-amber-800">
                  Se unirán {dups.length} grupo(s). Antes de unificar, revisá qué registros se combinan:
                </p>
                <ul className="space-y-1.5 max-h-40 overflow-y-auto">
                  {dups.map((g) => (
                    <li key={g.key} className="rounded-md border border-amber-100 bg-amber-50/50 px-2.5 py-1.5 text-xs">
                      <span className="text-slate-500">{g.variants.join("  ·  ")}</span>
                      <span className="block text-emerald-800 font-medium mt-0.5">→ se conserva: {g.keep}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2">
                  <Button type="button" size="sm" data-testid="vendedores-apply-unify"
                    onClick={applyUnify} disabled={unifying} className="flex-1 bg-amber-600 hover:bg-amber-700">
                    {unifying ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <GitMerge className="h-4 w-4 mr-1.5" />}
                    Unificar duplicados
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => setDups(null)} className="border-slate-200">
                    Cancelar
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
